import React, { useState, useEffect } from 'react';
import { FaUnlock, FaUserSlash, FaSearch } from 'react-icons/fa';
import api from '../../services/api';
import { showError, showSuccess } from '../../utils/toast';
import SkeletonLoader from '../common/SkeletonLoader';

const BlockedUsers = () => {
    const [blockedUsers, setBlockedUsers] = useState([]);
    const [loading, setLoading] = useState(true);
    const [search, setSearch] = useState('');

    useEffect(() => {
        fetchBlockedUsers();
    }, []);

    const fetchBlockedUsers = async () => {
        setLoading(true);
        try {
            const res = await api.get('/blocked-users');
            setBlockedUsers(res.data);
        } catch (error) {
            console.error(error);
            showError('Failed to fetch blocked users');
        } finally {
            setLoading(false);
        }
    };

    const handleUnblock = async (userId) => {
        try {
            await api.post('/unblock', { userId });
            showSuccess('User unblocked successfully');
            setBlockedUsers(blockedUsers.filter(u => u._id !== userId));
        } catch (error) {
            showError('Failed to unblock user');
        }
    };

    const filteredUsers = blockedUsers.filter(u =>
        u.name?.toLowerCase().includes(search.toLowerCase()) ||
        u.email?.toLowerCase().includes(search.toLowerCase())
    );

    if (loading) return <SkeletonLoader count={3} type="list" />;

    return (
        <div className="max-w-4xl mx-auto p-6 bg-white rounded-3xl shadow-sm border border-gray-100">
            <h2 className="text-2xl font-bold text-gray-800 mb-6 flex items-center gap-2">
                <FaUserSlash className="text-red-500" /> Blocked Users
            </h2>

            <div className="mb-6 relative">
                <FaSearch className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                <input
                    type="text"
                    placeholder="Search blocked users..."
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                    className="w-full pl-10 pr-4 py-3 bg-gray-50 border border-gray-200 rounded-xl focus:ring-2 focus:ring-rose-200 focus:border-rose-400 outline-none transition-all"
                />
            </div>

            {filteredUsers.length === 0 ? (
                <div className="text-center py-12 text-gray-400">
                    <FaUserSlash size={48} className="mx-auto mb-3 opacity-20" />
                    <p>No blocked users found.</p>
                </div>
            ) : (
                <div className="space-y-4">
                    {filteredUsers.map(user => (
                        <div key={user._id} className="flex flex-col sm:flex-row items-center justify-between p-4 rounded-2xl border border-gray-100 hover:shadow-md transition-all gap-4">
                            <div className="flex items-center gap-4 w-full sm:w-auto">
                                <div className="w-12 h-12 rounded-full bg-gray-200 flex items-center justify-center text-gray-500 font-bold text-xl">
                                    {user.name?.[0]?.toUpperCase() || 'U'}
                                </div>
                                <div>
                                    <h4 className="font-bold text-gray-800">{user.name}</h4>
                                    <p className="text-sm text-gray-500">{user.email}</p>
                                </div>
                            </div>
                            <button
                                onClick={() => handleUnblock(user._id)}
                                className="w-full sm:w-auto px-5 py-2 bg-gray-900 text-white text-sm font-bold rounded-xl hover:bg-gray-800 transition-colors flex items-center justify-center gap-2"
                            >
                                <FaUnlock size={12} /> Unblock
                            </button>
                        </div>
                    ))}
                </div>
            )}
        </div>
    );
};

export default BlockedUsers;
